import { type PendingTransferReversal, Query } from "@gen/app-query";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { GridColDef } from "@mui/x-data-grid-pro";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

import { CollapsibleSection } from "~/components/collapsible_section";
import { LedgerAmount } from "~/components/ledger_amount";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { ReadableDataGrid } from "~/components/readable_data_grid";
import { Money } from "~/utilities/money";

export interface OutstandingRefundCostsProps {
  clientId: string;
  // `debtThreshold` is the refund overdraft limit of the client, in the
  // smallest unit of the default currency of the client's connected account.
  debtThreshold?: number;
}

function toOutstandingMoney(amount: number, currency: string) {
  return new Money(-amount, currency);
}

// `sumByCurrency` returns the total amount of the pending transfer reversals
// for each currency, in the order in which the currencies first appear.
function sumByCurrency(reversals: PendingTransferReversal[]) {
  const totals = new Map<string, number>();
  for (const reversal of reversals) {
    const currency = reversal.currency.toUpperCase();
    totals.set(currency, (totals.get(currency) ?? 0) + reversal.amount);
  }
  return [...totals.entries()].map(([currency, amount]) => ({
    currency,
    money: toOutstandingMoney(amount, currency),
  }));
}

export function OutstandingRefundCosts({
  clientId,
  debtThreshold,
}: OutstandingRefundCostsProps) {
  const { data: reversalsData, isFetching } =
    Query.useCore_getClientPendingTransferReversalsQuery(clientId);

  const { data: connectedAccountData } =
    Query.useCore_getClientConnectedAccountQuery(clientId, {
      enabled: debtThreshold !== undefined,
    });
  const debtThresholdCurrency =
    connectedAccountData?.default_currency?.toUpperCase();

  const reversals = useMemo(() => reversalsData ?? [], [reversalsData]);
  const totals = useMemo(() => sumByCurrency(reversals), [reversals]);

  const columns = useMemo<GridColDef<PendingTransferReversal>[]>(
    () => [
      {
        field: "transaction_id",
        headerName: "Transaction ID",
        minWidth: 160,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <MuiLink
            component={Link}
            to={`/clients/${clientId}/f2f/transactions/${params.row.transaction_id}`}
            variant="body2"
            fontWeight="bold"
          >
            {params.row.transaction_id}
          </MuiLink>
        ),
      },
      {
        field: "amount",
        headerName: "Refund Amount",
        minWidth: 160,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <LedgerAmount
            amount={toOutstandingMoney(params.row.amount, params.row.currency)}
          />
        ),
      },
    ],
    [clientId],
  );

  if (reversals.length === 0) {
    return null;
  }

  return (
    <CollapsibleSection
      id="outstanding_refund_costs"
      iconName="sync"
      title="Outstanding Refund Costs"
      count={reversals.length}
      summary={
        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="flex-end"
          columnGap={2}
        >
          {totals.map(({ currency, money }) => (
            <LedgerAmount
              key={currency}
              amount={money}
              variant="h5"
              component="span"
            />
          ))}
        </Stack>
      }
      dataTestId="outstanding-refund-costs"
    >
      <Stack spacing={2.5}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Outstanding refund costs are processed in order of smallest to
          largest.
        </Typography>
        <Box sx={{ backgroundColor: "common.white", borderRadius: 2 }}>
          <ReadableDataGrid<PendingTransferReversal>
            dataTestId="outstanding-refund-costs-grid"
            storageKey="OutstandingRefundCosts"
            // The theme pads the footer for standalone grids, but here the section's
            // own bottom padding already provides the spacing.
            sx={{ "& .MuiDataGrid-footerContainer": { pb: 0 } }}
            columns={columns}
            rows={reversals}
            getRowId={(row) => row.id}
            loading={isFetching}
            hideToolbar
            autoHeight
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            localeText={{
              noRowsLabel: "No Outstanding Refund Costs",
            }}
          />
        </Box>
        {debtThreshold !== undefined && debtThresholdCurrency && (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ color: "text.secondary" }}
          >
            <PublicSvgIcon
              name="info_outlined"
              inheritColor
              sx={{ display: "flex", flexShrink: 0 }}
            />
            <Typography
              variant="body2"
              data-testid="outstanding-refund-costs-debt_threshold-text"
            >
              The refund overdraft limit for your account is{" "}
              {new Money(debtThreshold, debtThresholdCurrency).format()}. To
              request a higher limit, please contact your account manager.
            </Typography>
          </Stack>
        )}
      </Stack>
    </CollapsibleSection>
  );
}
